import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'

/**
 * The four layout primitives, so a screen is not a wall of flex utilities.
 *
 * Deliberately thin. These carry no look at all — no colour, no border, no
 * padding of their own — because the moment a layout primitive starts styling
 * things, every screen reaches for it instead of a component.
 */
const stack = cva('flex', {
  variants: {
    direction: { column: 'flex-col', row: 'flex-row items-center' },
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      5: 'gap-5',
      6: 'gap-6',
      8: 'gap-8',
      10: 'gap-10',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    wrap: { true: 'flex-wrap', false: '' },
  },
  defaultVariants: { direction: 'column', gap: 4, wrap: false },
})

export interface StackProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof stack> {
  /**
   * A `<form>` where the column **is** a form.
   *
   * The sign-in page's three steps are each a column of fields and a button,
   * and each one was a `<div>` — so Enter did nothing, and no browser could
   * see a form to offer to save the password into. Wrapping them in an extra
   * element would have put a `display: block` between the column and its
   * gaps; this makes the column itself the form.
   *
   * Only `form`, because that is the only element with behaviour the layout
   * has a reason to borrow.
   */
  as?: 'form'
  onSubmit?: React.FormEventHandler<HTMLElement>
}

/** A column. The default arrangement for everything on these screens. */
export function Stack({
  className,
  direction,
  gap,
  align,
  justify,
  wrap,
  as,
  ...props
}: StackProps) {
  const Comp = (as ?? 'div') as 'div'
  return (
    <Comp className={cn(stack({ direction, gap, align, justify, wrap }), className)} {...props} />
  )
}

/** A row, vertically centred. */
export function Row({ className, gap = 3, ...props }: Omit<StackProps, 'direction'>) {
  return <Stack direction="row" gap={gap} className={className} {...props} />
}

/** A row with the first thing left and the last thing right. */
export function RowBetween({
  className,
  gap = 4,
  ...props
}: Omit<StackProps, 'direction' | 'justify'>) {
  return <Stack direction="row" gap={gap} justify="between" className={className} {...props} />
}

/** Takes the space between two things in a row. */
export function Fill({ className }: { className?: string }) {
  return <div className={cn('flex-1', className)} aria-hidden />
}

/** A responsive grid of equal columns — the four figures, the two lists under them. */
export function Grid({
  columns = 2,
  gap = 4,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { columns?: 2 | 3 | 4; gap?: 2 | 3 | 4 | 5 | 6 }) {
  return (
    <div
      className={cn(
        'grid',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        columns === 4 && 'grid-cols-2 lg:grid-cols-4',
        gap === 2 && 'gap-2',
        gap === 3 && 'gap-3',
        gap === 4 && 'gap-4',
        gap === 5 && 'gap-5',
        gap === 6 && 'gap-6',
        className,
      )}
      {...props}
    />
  )
}
